import { useEffect, useRef } from 'react';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { useActiveBundle } from '@/context/active-bundle';
import { AppBundle } from '@/utils/app-bundles';

import { useLocalStorage } from '@/hooks/browser/use-local-storage';

import { Box, SideNav, useSideNavState } from '@customerio/pluma-components/react';
import { PlumaSideNav as PlumaSideNavCss } from '@customerio/pluma-components/css/components';
import { shouldShowDevHelper } from '@/layouts/root/components/dev-helper/store';
import { DevToolsNavButton } from './dev-tools-nav-button';

export const NAV_COLLAPSED_LOCAL_STORAGE_KEY = 'hydra:is-main-nav-collapsed';

const NAV_WIDTHS = {
  collapsed: Number.parseInt(PlumaSideNavCss.values.collapsedWidth),
  expanded: Number.parseInt(PlumaSideNavCss.values.expandedWidth),
};

type MainNavigationWrapperProps = {
  isCollapsible?: boolean;
  children?: React.ReactNode;
};

export function MainNavigationWrapper(props: MainNavigationWrapperProps) {
  const { isCollapsible = true, children } = props;

  const activeApp = useActiveBundle();
  const themeContext = useThemeContext();
  const sideNavState = useSideNavState();
  const sideNavRef = useRef<HTMLElement>(null);

  const [, setIsNavCollapsed] = useLocalStorage<boolean>(NAV_COLLAPSED_LOCAL_STORAGE_KEY, false);

  useEffect(() => {
    if (sideNavState != null) {
      setIsNavCollapsed(sideNavState.isCollapsed);
    }
  }, [setIsNavCollapsed, sideNavState]);

  useEffect(() => {
    const navWidth = sideNavState?.isCollapsed === true ? NAV_WIDTHS.collapsed : NAV_WIDTHS.expanded;
    themeContext.setVariable(GlobalCSSVariable.MainNavWidth, `${navWidth}px`);
  }, [sideNavState?.isCollapsed, themeContext]);

  return (
    <SideNav
      ref={sideNavRef}
      isCollapsible={isCollapsible}
      isCollapsed={!isCollapsible ? false : undefined}
      overflowY="auto"
      overflowX="hidden"
      aria-label="Main"
      style={{ scrollbarWidth: 'thin' }}
      pt="0"
    >
      {children}

      <Box mt="auto">
        {Object.values(AppBundle).map((bundle) => (
          <div
            key={bundle}
            id={`hydra-main-nav-footer-portal-${bundle}`}
            style={{ ...(activeApp !== bundle ? { display: 'none' } : {}) }}
          />
        ))}
        {shouldShowDevHelper() ? <DevToolsNavButton sideNavRef={sideNavRef} isCollapsible={isCollapsible} /> : null}
      </Box>
    </SideNav>
  );
}
